import { AppPathsMap } from '@core/routing/app-paths';

/**
 * Query parameter that carries the post-login destination on the login URL.
 *
 * The login URL is the only place this can live: the post-login step is a
 * full-page load (see `NavigationService.goToWithReload`), so nothing held in
 * memory survives it, and the URL travels with the user through the whole flow.
 */
const returnUrlParameter = 'redirect';

/**
Upper bound so a hand-crafted URL cannot bloat the login link.
 */
const maxReturnUrlLength = 2000;

/**
 * Routes that must never be used as a post-login destination: returning to the
 * login page would loop, and the logout/error pages only make sense without a
 * session. When one of these is requested the caller falls back to the home route.
 */
const nonDestinationPaths: readonly string[] = [
  AppPathsMap.Login,
  AppPathsMap.Logout,
  ...Object.values(AppPathsMap.Error),
];

/**
 * Whether the value contains whitespace or control characters. Rejecting these
 * outright keeps CR/LF and NUL out of the value we later hand to
 * `location.assign`.
 *
 * @param {string} value - The candidate URL.
 * @returns {boolean} `true` when at least one character is unsafe.
 */
function hasUnsafeCharacters(value: string): boolean {
  return [...value].some((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code <= 0x20 || code === 0x7f;
  });
}

/**
 * Strips the query string and fragment, leaving the path.
 *
 * @param {string} url - The internal URL.
 * @returns {string} The path portion.
 */
function pathOf(url: string): string {
  return url.split(/[?#]/, 1)[0] ?? '';
}

/**
 * Whether the path is one we must not send an authenticated user back to.
 * Matched with `endsWith` as well as exactly, so a deployment under a base href
 * still resolves (mirrors `isOnAnonymousAccessiblePage`).
 *
 * @param {string} path - The path to test.
 * @returns {boolean} `true` when the path is not a usable destination.
 */
function isNonDestination(path: string): boolean {
  return (
    path === '/' || nonDestinationPaths.some((candidate) => path === `/${candidate}` || path.endsWith(`/${candidate}`))
  );
}

/**
 * Sanitizes a post-login destination.
 *
 * Only app-internal paths are accepted — a single leading slash, no backslashes.
 * `//host` and `/\host` are protocol-relative URLs the browser would resolve to a
 * foreign origin, and anything carrying a scheme fails the leading-slash test, so
 * the value can never turn the login page into an open redirect.
 *
 * @param {string | null | undefined} rawUrl - The raw candidate (query parameter or router URL).
 * @returns {string | undefined} The path to resume, or undefined when there is none to honour.
 */
export function sanitizeReturnUrl(rawUrl: string | null | undefined): string | undefined {
  if (!rawUrl || rawUrl.length > maxReturnUrlLength || rawUrl.includes('\\') || hasUnsafeCharacters(rawUrl)) {
    return undefined;
  }
  if (!rawUrl.startsWith('/') || rawUrl.startsWith('//')) {
    return undefined;
  }
  return isNonDestination(pathOf(rawUrl)) ? undefined : rawUrl;
}

/**
 * Reads the post-login destination from the current location's query string.
 *
 * Deliberately reads `globalThis.location` rather than the router: the login
 * completion runs outside any route component and must also work before the
 * router has settled.
 *
 * @returns {string | undefined} The sanitized destination, or undefined when absent/rejected.
 */
export function readReturnUrlFromLocation(): string | undefined {
  try {
    return sanitizeReturnUrl(new URLSearchParams(globalThis.location.search).get(returnUrlParameter));
  } catch {
    return undefined;
  }
}

/**
 * Captures the URL the visitor is currently on as a post-login destination.
 * Used during bootstrap, where an anonymous visitor arriving on a deep link is
 * redirected to the login page before any route has activated.
 *
 * @returns {string | undefined} The sanitized current URL, or undefined when it is not worth resuming.
 */
export function currentReturnUrl(): string | undefined {
  try {
    const { pathname, search, hash } = globalThis.location;
    return sanitizeReturnUrl(`${pathname}${search}${hash}`);
  } catch {
    return undefined;
  }
}

export { returnUrlParameter as ReturnUrlParameter };
